import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs } from "../../../../ui/components/Tabs";
import { FeatherInbox, FeatherUsers } from "@subframe/core";
import { BotMailboxPage } from "./BotMailboxPage";
import { BotZohoUsersPage } from "./BotZohoUsersPage";

/**
 * "Zoho Books Automation Accounts" — the tab shell.
 *
 * Two sections: Mail box and Manage users. The active section rides in a
 * `sub` query param so a screen is linkable and survives a refresh, matching
 * how AdminDashboard keeps its own tab in `?tab=`.
 */

type BotSection = "mailbox" | "users";

const VALID_SECTIONS: BotSection[] = ["mailbox", "users"];

export const ZohoBotPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const readSection = useCallback((): BotSection => {
    const sub = searchParams.get("sub");
    return sub && VALID_SECTIONS.includes(sub as BotSection)
      ? (sub as BotSection)
      : "mailbox";
  }, [searchParams]);

  const [section, setSection] = useState<BotSection>(readSection);

  useEffect(() => {
    const next = readSection();
    setSection((prev) => (next !== prev ? next : prev));
  }, [readSection]);

  const goTo = (next: BotSection): void => {
    setSection(next);
    // Keep every other param (notably ?tab=) so this only moves the sub-tab.
    const params = new URLSearchParams(window.location.search);
    params.set("sub", next);
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
      <div className="flex w-full flex-col items-start gap-1 border-b border-solid border-neutral-border px-8 pt-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Zoho Books Automation Accounts
        </span>
        <span className="text-body font-body text-subtext-color">
          The accounts the Zoho Books bot runs on.
        </span>
        <div className="mt-4 w-full">
          <Tabs>
            <Tabs.Item active={section === "mailbox"} onClick={() => goTo("mailbox")}>
              <FeatherInbox className="h-4 w-4" />
              Mail box
            </Tabs.Item>
            <Tabs.Item active={section === "users"} onClick={() => goTo("users")}>
              <FeatherUsers className="h-4 w-4" />
              Manage users
            </Tabs.Item>
          </Tabs>
        </div>
      </div>

      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-auto">
        {section === "mailbox" ? <BotMailboxPage /> : <BotZohoUsersPage />}
      </div>
    </div>
  );
};

export default ZohoBotPage;
